import { useCallback, useEffect, useRef, useState } from 'react';
import { useRouter, useSearch } from '@tanstack/react-router';
import { useDebounce } from '@r/hooks/use-debounce.ts';
import { formatBrandIdsForSearch, parseBrandIdsFromSearch } from '@r/helpers/product-catalog.ts';
import type { ProductCategoryProductCategoryReadModel } from '@r/api/generated/client.schemas.ts';

type ProductCategory = ProductCategoryProductCategoryReadModel;

type SearchParams = {
  subcategoryId?: string | number;
  categoryId?: string | number;
  brandIds?: string | number | Array<string | number>;
  search?: string;
  [key: string]: unknown;
};

interface UseProductFiltersProps {
  productCategories: ProductCategory[];
  /** IDs des marques disponibles pour la catégorie courante (undefined tant que la liste n'est pas chargée). */
  availableBrandIds?: number[];
}

/**
 * Centralise les filtres du catalogue produits et leur persistance dans l'URL.
 * Gère la recherche, les catégories et les marques pour garder desktop/mobile synchronisés.
 */
export const useProductFilters = ({
  productCategories,
  availableBrandIds
}: UseProductFiltersProps) => {
  const { navigate, state } = useRouter();
  const location = state.location;
  const search = useSearch({ strict: false }) as SearchParams;

  const categoryFromUrl = search.categoryId ? Number(search.categoryId) : null;
  const subCategoryFromUrl = search.subcategoryId ? Number(search.subcategoryId) : null;
  const brandIdsFromUrl = parseBrandIdsFromSearch(search.brandIds);

  const [searchInput, setSearchInput] = useState<string>(
    typeof search.search === 'string' ? search.search : ''
  );
  const debouncedSearch = useDebounce(searchInput, 500);
  const searchInputSourceRef = useRef<'user' | 'url'>('url');

  const updateSearchInput = useCallback((value: string) => {
    searchInputSourceRef.current = 'user';
    setSearchInput(value);
  }, []);

  useEffect(() => {
    const searchValue = typeof search.search === 'string' ? search.search : '';
    setSearchInput((current) => {
      if (current === searchValue) return current;
      return searchValue;
    });
    searchInputSourceRef.current = 'url';
  }, [search.search]);

  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(categoryFromUrl);
  const [selectedSubCategoryId, setSelectedSubCategoryId] = useState<number | null>(
    subCategoryFromUrl
  );
  const [openCategoryIds, setOpenCategoryIds] = useState<number[]>([]);

  useEffect(() => {
    setSelectedCategoryId(categoryFromUrl);
    setSelectedSubCategoryId(subCategoryFromUrl);
  }, [categoryFromUrl, subCategoryFromUrl]);

  useEffect(() => {
    const nextOpen: number[] = [];
    if (categoryFromUrl) {
      nextOpen.push(categoryFromUrl);
    }
    if (subCategoryFromUrl) {
      const parent = productCategories.find((cat) =>
        cat.childs?.some((child) => child.id === subCategoryFromUrl)
      );
      if (parent) nextOpen.push(parent.id);
    }
    setOpenCategoryIds(Array.from(new Set(nextOpen)));
  }, [categoryFromUrl, subCategoryFromUrl, productCategories]);

  // Retire les marques sélectionnées qui ne sont plus disponibles après un changement de catégorie.
  useEffect(() => {
    if (availableBrandIds === undefined) return;

    const currentBrandIds = parseBrandIdsFromSearch(search.brandIds);
    const availableSet = new Set(availableBrandIds);
    const validBrandIds = currentBrandIds.filter((id) => availableSet.has(id));
    if (validBrandIds.length === currentBrandIds.length) return;

    navigate({
      to: location.pathname,
      search: (prev: SearchParams) => ({
        ...prev,
        brandIds: formatBrandIdsForSearch(validBrandIds),
        page: 1
      }),
      replace: true
    });
  }, [availableBrandIds, search.brandIds, location.pathname, navigate]);

  // Persiste la recherche après debounce pour éviter de requêter à chaque frappe.
  useEffect(() => {
    if (searchInputSourceRef.current !== 'user') return;
    if (debouncedSearch !== searchInput) return;

    const current = typeof search.search === 'string' ? search.search : '';
    const searchTerm = (debouncedSearch || '').trim();
    if (searchTerm === (current || '')) return;

    navigate({
      to: location.pathname,
      search: (prev: SearchParams) => ({ ...prev, search: searchTerm || undefined, page: 1 }),
      replace: true
    });
  }, [debouncedSearch, location.pathname, navigate, search.search, searchInput]);

  const setCategoryOpen = (categoryId: number, isOpen: boolean) => {
    setOpenCategoryIds((prev) => {
      if (isOpen) {
        if (prev.includes(categoryId)) return prev;
        return [...prev, categoryId];
      }
      return prev.filter((id) => id !== categoryId);
    });
  };

  const handleSelectCategory = (id: number) => {
    const isSame = selectedCategoryId === id;
    const newSelectedId = isSame ? null : id;

    setSelectedCategoryId(newSelectedId);
    setSelectedSubCategoryId(null);
    setCategoryOpen(id, true);

    navigate({
      to: location.pathname,
      search: (prev: SearchParams) => {
        if (isSame) {
          const updated = { ...prev };
          delete (updated as Record<string, unknown>).categoryId;
          delete (updated as Record<string, unknown>).subcategoryId;
          return { ...updated, page: 1 };
        }
        const updated = { ...prev };
        delete (updated as Record<string, unknown>).subcategoryId;
        return { ...updated, categoryId: id, page: 1 };
      }
    });
  };

  const handleSelectSubCategory = (id: number) => {
    const isSame = selectedSubCategoryId === id;
    const newSelectedId = isSame ? null : id;

    setSelectedSubCategoryId(newSelectedId);
    setSelectedCategoryId(null);
    const parent = productCategories.find((cat) => cat.childs?.some((child) => child.id === id));
    if (parent) setCategoryOpen(parent.id, true);

    navigate({
      to: location.pathname,
      search: (prev: SearchParams) => {
        if (isSame) {
          const updated = { ...prev };
          delete (updated as Record<string, unknown>).subcategoryId;
          delete (updated as Record<string, unknown>).categoryId;
          return { ...updated, page: 1 };
        }
        const updated = { ...prev };
        delete (updated as Record<string, unknown>).categoryId;
        return { ...updated, subcategoryId: id, page: 1 };
      }
    });
  };

  const handleResetCategories = () => {
    setSelectedCategoryId(null);
    setSelectedSubCategoryId(null);
    setOpenCategoryIds([]);
    navigate({
      to: location.pathname,
      search: (prev: SearchParams) => {
        const updated = { ...prev };
        delete (updated as Record<string, unknown>).categoryId;
        delete (updated as Record<string, unknown>).subcategoryId;
        return { ...updated, page: 1 };
      },
      replace: true
    });
  };

  /** Ajoute ou retire une marque des filtres actifs et persiste la sélection dans l'URL. */
  const handleToggleBrand = (brandId: number) => {
    const isSelected = brandIdsFromUrl.includes(brandId);
    const nextBrandIds = isSelected
      ? brandIdsFromUrl.filter((id) => id !== brandId)
      : [...brandIdsFromUrl, brandId];

    navigate({
      to: location.pathname,
      search: (prev: SearchParams) => ({
        ...prev,
        brandIds: formatBrandIdsForSearch(nextBrandIds),
        page: 1
      }),
      replace: true
    });
  };

  return {
    searchInput,
    setSearchInput: updateSearchInput,
    selectedCategoryId,
    selectedSubCategoryId,
    selectedBrandIds: brandIdsFromUrl,
    openCategoryIds,
    setCategoryOpen,
    handleSelectCategory,
    handleSelectSubCategory,
    handleResetCategories,
    handleToggleBrand
  };
};
